/**
 * The Science.co growth model, as code.
 *
 * One function runs it two ways:
 *
 *   PLAN        runModel(assumptions): the spreadsheet, month by month, from
 *               nothing but assumptions.
 *   RE-FORECAST runModel(assumptions, { actuals }): the same arithmetic, but
 *               every month that has happened is REPLACED by what happened,
 *               and the months after it are projected from there. Customers
 *               who actually signed up renew on the model's retention curve;
 *               customers the plan expected and never got do not. The current
 *               month is paced to a full month — on the plan's own daily
 *               ramp, see rampWeights — so the forecast has a whole month to
 *               grow from, rather than a half one.
 *
 * So the re-forecast "waterfalls" out of the actuals: its line runs along the
 * actual bars and then carries on from where they end.
 *
 * DIFFERENCES FROM THE SPREADSHEET, deliberately
 *
 *  - Semaglutide revenue. Every revenue formula in the Assumptions sheet
 *    multiplies semaglutide prices by (0.4 + customers) where it means
 *    (0.4 × customers): `(C$39+C71)` for `(C71*C$39)`. That bills semaglutide
 *    to every customer rather than 40% of them and overstates revenue by about
 *    $70M of $227M. This computes the intended formula.
 *  - Retention follows the curves the sheet STATES (Recurring Cohorts rows
 *    3–6) to their end. The sheet's 3-month cohort formulas stop at age six,
 *    so its 10% renewal at age nine never lands; here it does, from month ten.
 *  - Commissions are paid on revenue in every month. The sheet's first month
 *    pays them on net income instead, which is the only column that does.
 *  - Commissions are a COST. The sheet's "COS - Commissions VAR" line is
 *    organic ad spend less commissions, which both credits commissions back
 *    to profit and charges organic spend twice, since the full ad spend is an
 *    expense below it. See the gross profit calculation.
 */

import { PLANS, BOOKS, MOLECULES, MODEL_MONTHS } from '../config/scienceModel'

/** 'YYYY-MM' plus n months. */
export function addMonths(month, n) {
  const [year, mon] = month.split('-').map(Number)
  const total = year * 12 + (mon - 1) + n
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`
}

/** Whole months from `from` to `to`; negative when `to` is earlier. */
export function monthsBetween(from, to) {
  const [fy, fm] = from.split('-').map(Number)
  const [ty, tm] = to.split('-').map(Number)
  return (ty - fy) * 12 + (tm - fm)
}

const at = (series, index) => (Array.isArray(series) ? (series[index] ?? 0) : (series ?? 0))

const emptyPlans = () => Object.fromEntries(PLANS.map(plan => [plan, 0]))

const sumPlans = (byPlan) => PLANS.reduce((sum, plan) => sum + (byPlan[plan] || 0), 0)

/**
 * What one charge on `plan` brings in during model month `m`, averaged over
 * the molecule mix and that month's price-book weights.
 */
export function blendedPrice(a, m, plan) {
  let price = 0
  for (const molecule of MOLECULES) {
    const share = a.moleculeMix[molecule] || 0
    for (const book of BOOKS) {
      price += share * at(a.bookWeights[book], m) * (a.prices[molecule]?.[plan]?.[book] || 0)
    }
  }
  return price
}

export function daysInMonth(month) {
  const [year, mon] = month.split('-').map(Number)
  return new Date(Date.UTC(year, mon, 0)).getUTCDate()
}

/**
 * How each month's total spreads across its days, as weights summing to 1.
 *
 * The model grows fast — spend is up 65% in month two — so an even split would
 * put too much of a month's target in its first days and too little in its
 * last, and every partial month would read behind plan early and ahead late.
 * Instead the daily rate ramps in a straight line from the pace where the
 * previous month ended toward the pace the next month starts at: boundary
 * rates are the midpoints of neighbouring months' daily averages, and the
 * outer edges are extrapolated so each month's own average is kept.
 * Normalising per month keeps every monthly total exactly what the model says.
 *
 * @param {number[]} totals      one total per model month (the plan's spend)
 * @param {string}   startMonth  'YYYY-MM' of totals[0]
 * @returns {number[][]} weights[m][day - 1]
 */
export function rampWeights(totals, startMonth) {
  const n = totals.length
  const lengths = totals.map((_, m) => daysInMonth(addMonths(startMonth, m)))
  const average = totals.map((total, m) => total / lengths[m])
  const edge = []
  for (let m = 1; m < n; m++) edge[m] = (average[m - 1] + average[m]) / 2
  edge[0] = n > 1 ? Math.max(0, 2 * average[0] - edge[1]) : average[0]
  edge[n] = n > 1 ? Math.max(0, 2 * average[n - 1] - edge[n - 1]) : average[n - 1]

  return lengths.map((length, m) => {
    const raw = Array.from({ length }, (_, i) => Math.max(0, edge[m] + (edge[m + 1] - edge[m]) * ((i + 0.5) / length)))
    const sum = raw.reduce((a, b) => a + b, 0)
    return sum > 0 ? raw.map(w => w / sum) : raw.map(() => 1 / length)
  })
}

/**
 * Share of a month's plan that falls in its first `elapsed` days. Fractional
 * days count part of the day in progress, so 16.5 is halfway through the 17th.
 */
export function shareToDate(weights, elapsed) {
  const full = Math.max(0, Math.min(Math.floor(elapsed), weights.length))
  let share = 0
  for (let i = 0; i < full; i++) share += weights[i]
  if (full < weights.length) share += weights[full] * Math.max(0, elapsed - full)
  return share
}

/** The plan's own spend line, which the re-forecast can follow instead of compounding actuals. */
export function plannedSpend(a) {
  const spend = []
  for (let m = 0; m < MODEL_MONTHS; m++) {
    spend.push(m === 0 ? a.acquisition.startingSpend : spend[m - 1] * (1 + at(a.acquisition.spendGrowth, m)))
  }
  return spend
}

/** Recurring payers in `month` from every cohort that bought before it. */
function recurringFrom(cohorts, month, retention) {
  const byPlan = emptyPlans()
  for (const cohort of cohorts) {
    const age = monthsBetween(cohort.month, month)
    if (age <= 0) continue
    byPlan[cohort.plan] += cohort.size * at(retention[cohort.plan], age)
  }
  return byPlan
}

/**
 * Run the model.
 *
 * @param {object} a  assumptions, shaped like SCIENCE_MODEL_DEFAULTS
 * @param {object} [options]
 * @param {object} [options.actuals]  the /science-model/actuals payload. When
 *   present this is a re-forecast; when absent it is the plan.
 * @param {'plan'|'actuals'} [options.spendMode]  how a re-forecast sets future
 *   spend: follow the plan's budget, or compound the growth rates from the
 *   last actual month.
 * @param {boolean} [options.pace]  false leaves the month in progress as
 *   recorded so far. That is what the Actual column shows; only the forecast
 *   needs a whole month.
 * @returns {object[]} one row per model month
 */
export function runModel(a, { actuals = null, spendMode = 'plan', pace: paceCurrent = true } = {}) {
  const budget = plannedSpend(a)
  const weights = rampWeights(budget, a.startMonth)
  const actualMonths = actuals?.months || {}

  // Customers who bought before the model started still renew inside it.
  const cohorts = actuals
    ? (actuals.cohorts || [])
      .filter(c => monthsBetween(a.startMonth, c.month) < 0 && PLANS.includes(c.plan))
      .map(c => ({ month: c.month, plan: c.plan, size: c.started }))
    : []

  const rows = []
  for (let m = 0; m < MODEL_MONTHS; m++) {
    const month = addMonths(a.startMonth, m)
    const actual = actualMonths[month]
    const source = !actual ? 'forecast' : actual.complete ? 'actual' : 'paced'
    // A month in progress is scaled by the share of its plan that should have
    // happened by now, on the same daily ramp the Daily view draws — not by
    // days elapsed, which would call a growing month behind all month.
    const shareSoFar = paceCurrent && source === 'paced' ? shareToDate(weights[m], actual.days_elapsed) : 1
    const pace = shareSoFar > 0 ? 1 / shareSoFar : 1
    const paced = (value) => (value == null ? null : value * pace)

    // ── Acquisition ────────────────────────────────────────────────────
    let spend
    if (source !== 'forecast') {
      spend = paced(actual.ad_spend) ?? 0
    } else if (actuals && spendMode === 'actuals' && m > 0) {
      spend = rows[m - 1].spend * (1 + at(a.acquisition.spendGrowth, m))
    } else {
      spend = budget[m]
    }

    const paidShare = at(a.acquisition.paidShare, m)
    const paidSpend = spend * paidShare
    const organicSpend = spend * (1 - paidShare)

    let newByPlan
    let newCustomers
    if (source === 'forecast') {
      const acquired = paidSpend / at(a.acquisition.cacPaid, m) + organicSpend / at(a.acquisition.cacOrganic, m)
      newByPlan = Object.fromEntries(PLANS.map(plan => [plan, acquired * (a.planSplit[plan] || 0)]))
      newCustomers = acquired
    } else if (actual.new_by_plan) {
      newByPlan = Object.fromEntries(PLANS.map(plan => [plan, paced(actual.new_by_plan[plan] || 0)]))
      newCustomers = paced(actual.new_customers) ?? sumPlans(newByPlan)
    } else {
      // No plan lengths (Manifold unreachable): the customers still happened,
      // so they are split on the assumed mix rather than renewing as nobody.
      newCustomers = paced(actual.new_customers) ?? 0
      newByPlan = Object.fromEntries(PLANS.map(plan => [plan, newCustomers * (a.planSplit[plan] || 0)]))
    }

    // Recurring payers come from cohorts that bought BEFORE this month, so
    // this month's cohort joins only after it has been counted.
    const recurringByPlan = recurringFrom(cohorts, month, a.retention)
    for (const plan of PLANS) cohorts.push({ month, plan, size: newByPlan[plan] })
    const recurringCustomers = source !== 'forecast' && actual.recurring_customers != null
      ? paced(actual.recurring_customers)
      : sumPlans(recurringByPlan)

    // ── Revenue ────────────────────────────────────────────────────────
    const priceByPlan = Object.fromEntries(PLANS.map(plan => [plan, blendedPrice(a, m, plan)]))
    let newRevenue
    let recurringRevenue
    if (source === 'forecast') {
      newRevenue = PLANS.reduce((sum, plan) => sum + newByPlan[plan] * priceByPlan[plan], 0)
      recurringRevenue = PLANS.reduce((sum, plan) => sum + recurringByPlan[plan] * priceByPlan[plan], 0)
    } else {
      newRevenue = paced(actual.new_revenue) ?? 0
      // A charge the report could not call new or renewal is still revenue.
      // It is counted as recurring so revenue sums to the report's total.
      recurringRevenue = (paced(actual.recurring_revenue) ?? 0) + (paced(actual.unattributed_revenue) ?? 0)
    }
    const revenue = newRevenue + recurringRevenue
    const refunds = source !== 'forecast' && actual.refunds != null
      ? paced(actual.refunds)
      : revenue * a.rates.refunds
    const income = revenue - refunds

    // ── Cost of goods ──────────────────────────────────────────────────
    const payingByPlan = Object.fromEntries(PLANS.map(plan => [plan, newByPlan[plan] + recurringByPlan[plan]]))
    const payers = sumPlans(payingByPlan)
    const cogsProduct = PLANS.reduce((sum, plan) => sum + payingByPlan[plan] * (a.unitCosts.product[plan] || 0), 0)
    const cogsOther = cogsProduct * a.rates.cogsOther
    const cogsShipping = payers * a.unitCosts.shipping
    const cogsDoctor = payers * a.unitCosts.doctorVisit
    // Actual COGS is the Daily Report's landed cost per shipment. It carries
    // no doctor-visit or "other" line, so an actual month has a total and no
    // breakdown — a modelled breakdown under an actual total would not sum.
    const cogsFromActual = source !== 'forecast' && actual.cogs_total != null
    const cogsTotal = cogsFromActual
      ? paced(actual.cogs_total)
      : cogsProduct + cogsOther + cogsShipping + cogsDoctor

    const salesShare = at(a.bookWeights.sales_team, m) + at(a.bookWeights.reddit, m)
    // Commissions are a cost, charged against gross profit.
    //
    // The sheet has them as "COS - Commissions VAR" = organic ad spend LESS
    // commissions, subtracted from gross profit. That is wrong twice over:
    // commissions come back as profit, so paying more of them raised it, and
    // organic spend is subtracted here as well as in the full ad spend below,
    // so it is counted against the business twice. Over the horizon the two
    // errors nearly cancelled, which is why the line looked harmless: $2.0M of
    // commissions offsetting $2.3M of organic spend to a net $310K.
    const commissions = a.rates.commission * salesShare * revenue
    const grossProfit = income - cogsTotal - commissions

    // ── Operating expenses ─────────────────────────────────────────────
    // No system records these, so they are the model's in every row.
    const expenses = {
      advertising: spend,
      agency: a.overrides.agency?.[m] ?? spend * a.rates.agency,
      management: a.overrides.management?.[m] ?? income * a.rates.management,
      merchant: income * a.rates.merchant,
      ...a.fixedMonthly,
    }
    const totalExpenses = Object.values(expenses).reduce((sum, v) => sum + (v || 0), 0)
    const netOperatingIncome = grossProfit - totalExpenses

    rows.push({
      month,
      index: m,
      source,
      pace,
      spend,
      paidSpend,
      organicSpend,
      newCustomers,
      newByPlan,
      recurringCustomers,
      recurringByPlan,
      payingCustomers: newCustomers + recurringCustomers,
      priceByPlan,
      newRevenue,
      recurringRevenue,
      revenue,
      refunds,
      income,
      newAov: newCustomers > 0 ? newRevenue / newCustomers : null,
      cac: newCustomers > 0 ? spend / newCustomers : null,
      cogsProduct: cogsFromActual ? null : cogsProduct,
      cogsOther: cogsFromActual ? null : cogsOther,
      cogsShipping: cogsFromActual ? null : cogsShipping,
      cogsDoctor: cogsFromActual ? null : cogsDoctor,
      cogsTotal,
      commissions,
      grossProfit,
      gpMargin: income > 0 ? grossProfit / income : null,
      expenses,
      totalExpenses,
      netOperatingIncome,
      noiMargin: income > 0 ? netOperatingIncome / income : null,
    })
  }
  return rows
}

/** Sum a field across rows. Nulls are skipped rather than treated as zero. */
export function sumField(rows, field) {
  return rows.reduce((sum, row) => sum + (row[field] ?? 0), 0)
}

/**
 * The model's retention curve against the actual one, per plan.
 *
 * Actual retention at age k is payers at age k over the cohort's size, pooled
 * across every cohort OLD ENOUGH to have reached age k — a cohort that bought
 * last week says nothing about month three and must not drag it to zero.
 */
export function retentionComparison(a, actuals, plan) {
  const curve = a.retention[plan] || []
  const cohorts = (actuals?.cohorts || []).filter(c => c.plan === plan && c.started > 0)
  const asOfMonth = actuals?.asOf?.slice(0, 7)
  return curve.map((modelRate, age) => {
    let started = 0
    let paying = 0
    for (const cohort of cohorts) {
      if (!asOfMonth || monthsBetween(cohort.month, asOfMonth) < age) continue
      // The month in progress has not finished billing, so it is not a
      // complete observation of that age either.
      if (monthsBetween(cohort.month, asOfMonth) === age && age > 0) continue
      started += cohort.started
      paying += cohort.paying?.[age] || 0
    }
    return {
      age,
      model: modelRate,
      actual: started > 0 ? paying / started : null,
      cohortSize: started,
    }
  })
}
